import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { useLogo, useSite } from "@/lib/store";
import { Switch } from "@/components/ui/switch";
import { Btn, Field, Loading, PageHead, Panel, SaveBtn, inputCls, useSiteDraft } from "@/admin/ui";

const SAMPLE = {
  name: "Priya Sharma", order_number: "VRV100123", status: "Processed", total: "₹6,199.00", note: "",
  items: "- WIN020LPC1 · JOHN DENVER – THE LAST RECORDINGS (LP) x 1", track_link: `${window.location.origin}/track?number=VRV100123`,
  tracking: "Courier tracking: Delhivery 1234567890 – https://www.delhivery.com/track/1234567890", tracking_link: "https://www.delhivery.com/track/1234567890",
};
const fill = (text, extra = {}) => Object.entries({ ...SAMPLE, ...extra }).reduce((t, [k, v]) => t.split(`{${k}}`).join(v), text || "").replace(/\n{3,}/g, "\n\n").trim();

function Preview({ tpl, buttons, status = SAMPLE.status }) {
  const LOGO = useLogo();
  return (
    <div className="bg-white border border-zinc-200 p-6 font-arial text-sm leading-relaxed" data-testid="email-preview">
      <p className="text-xs text-zinc-500 mb-4 border-b border-zinc-100 pb-3">Subject: <b className="text-black">{fill(tpl.subject, { status })}</b></p>
      <img src={LOGO} alt="Vinyl Record Vault" className="w-48 mb-6" />
      <div className="whitespace-pre-line">{fill(tpl.body, { status })}</div>
      {buttons && <div className="mt-5 flex gap-2">{buttons.map((b) => <span key={b} className="bg-black text-white font-bold px-4 py-2.5">{b}</span>)}</div>}
    </div>
  );
}

function TestSend({ tpl, buttons, status = SAMPLE.status, to, testid }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await adminApi.post("/emails/test", { to, subject: tpl.subject, body: tpl.body, status, buttons: !!buttons });
      toast.success(`Test email sent to ${to}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return <Btn variant="outline" className="h-9 px-4 self-start" disabled={busy || !to} onClick={send} data-testid={`${testid}-send-test`}>{busy ? "Sending…" : "Send test email"}</Btn>;
}

function Editor({ label, desc, tpl, onChange, buttons, status, testid, toggle, to }) {
  return (
    <Panel className="grid lg:grid-cols-2 gap-6" data-testid={testid}>
      <div className="space-y-4">
        <div>
          <h2 className="font-head font-bold text-lg">{label}</h2>
          {desc && <p className="text-xs text-zinc-500 mt-1">{desc}</p>}
        </div>
        {toggle}
        <Field label="Subject"><input value={tpl.subject} onChange={(e) => onChange({ ...tpl, subject: e.target.value })} className={inputCls} data-testid={`${testid}-subject`} /></Field>
        <Field label="Message"><textarea rows={12} value={tpl.body} onChange={(e) => onChange({ ...tpl, body: e.target.value })} className={`${inputCls} h-auto py-2 font-arial`} data-testid={`${testid}-body`} /></Field>
        <TestSend tpl={tpl} buttons={buttons} status={status} to={to} testid={testid} />
      </div>
      <Preview tpl={tpl} buttons={buttons} status={status} />
    </Panel>
  );
}

export default function Emails() {
  const { site } = useSite();
  const { draft, patch, save, saving } = useSiteDraft(["emails"]);
  const [stageId, setStageId] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => {
    adminApi.get("/me").then((r) => setTo((t) => t || r.data.email || "")).catch(() => {});
  }, []);
  if (!draft || !site) return <Loading />;
  const e = draft.emails;
  const set = (k, v) => patch("emails", { ...e, [k]: v });
  const stages = site.stages.filter((s) => s.id !== "pending");
  const cur = stages.find((s) => s.id === stageId) || stages[0];
  const ov = e.stage_overrides?.[cur?.id] || { enabled: false, subject: e.status.subject, body: e.status.body };
  const setOv = (v) => set("stage_overrides", { ...(e.stage_overrides || {}), [cur.id]: v });
  return (
    <div className="space-y-6">
      <PageHead title="Customer emails" desc="Plain-text emails with your logo on top. Placeholders: {name} {order_number} {status} {items} {total} {track_link} {tracking} {tracking_link} {note}. {items} lists each record as catalogue no. · artist – title (format) x qty. {tracking} is the courier line you save on each order (empty until saved). The note is what you type when updating an order.">
        <SaveBtn save={save} saving={saving} />
      </PageHead>
      <Panel className="flex flex-wrap items-end gap-3">
        <Field label="Send test emails to"><input value={to} onChange={(ev) => setTo(ev.target.value)} className={`${inputCls} w-80`} data-testid="email-test-to" /></Field>
        <p className="text-xs text-zinc-500 pb-2">Tests use the text on screen (even unsaved) with a sample order, and the subject starts with [TEST].</p>
      </Panel>
      <Editor label="Order confirmation" desc="Sent when payment is received." tpl={e.confirmation} onChange={(v) => set("confirmation", v)} testid="email-confirmation" status="Processing" to={to} />
      <Editor label="Status update (default)" desc="Sent when you move an order to a stage and leave 'Email the customer' on." tpl={e.status} onChange={(v) => set("status", v)} testid="email-status" to={to} />
      {cur && (
        <Editor label="Status update for a specific stage" testid="email-stage" tpl={ov} onChange={setOv} status={cur.name} to={to}
          desc="Optional: give one stage its own wording. When off, the default status email is used."
          toggle={(
            <div className="flex flex-wrap items-center gap-4">
              <select value={cur.id} onChange={(ev) => setStageId(ev.target.value)} className={`${inputCls} w-56`} data-testid="email-stage-select">
                {stages.map((s) => <option key={s.id} value={s.id}>{s.name}{e.stage_overrides?.[s.id]?.enabled ? " (custom)" : ""}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!ov.enabled} onCheckedChange={(v) => setOv({ ...ov, enabled: v })} data-testid="email-stage-enabled" /> Use custom email for this stage</label>
            </div>
          )} />
      )}
      <Editor label="Item not processed" desc="Sent when you mark one or more items as not processed. {items} lists only those items. The three choice buttons are added automatically." tpl={e.not_processed} onChange={(v) => set("not_processed", v)} buttons={["Get a refund", "I'll wait for it", "Choose an alternate"]} testid="email-not-processed" to={to} />
    </div>
  );
}
